// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    onNotesChange?: (updatedNotes: string) => void;
    notes: string;
    extendedStyles?: CartLineNotesStyles;
}

export interface CartLineNotesStyles {
    wrapper?: InjectableCss;
    toggleLink?: LinkPresentationProps;
    textAreaWrapper?: InjectableCss;
    editableNotesTextArea?: TextAreaProps;
}

export const cartLineNotesStyles: CartLineNotesStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: flex-end;
            flex-wrap: wrap;
            @media (max-width: 481px) {
                justify-content: flex-start;
            }
        `,
    },
    textAreaWrapper: {
        css: css`
            width: 100%;
            margin-top: 5px;
        `,
    },
    toggleLink: {
        typographyProps: {
            size: fontConfig.fontSize.xSmall,
            lineHeight: fontConfig.lineHeight.singleLinexSmall,
        },
        icon: {
            iconProps: {
                src: "Create",
                size: 14,
                color: "secondary.main",
                css: css`
                    margin-right: 4px;
                `,
            },
            position: "left",
        },
    },
    editableNotesTextArea: {
        cssOverrides: {
            inputSelect: css`
                resize: none;
                padding: 8px;
                font-size: ${fontConfig.fontSize.xSmall}px;
            `,
        },
    },
};

const WishlistLineNotes: FC<OwnProps> = ({ onNotesChange = () => {}, notes: lineNotes, extendedStyles }) => {
    const [showLineNote, setShowLineNotes] = useState(!!lineNotes);

    const lineNotesClickHandler = () => {
        setShowLineNotes(!showLineNote);
    };

    const [notes, setNotes] = useState(lineNotes);

    const changeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(event.currentTarget.value);
    };

    const blurHandler = () => {
        setShowLineNotes(!!notes);
        onNotesChange(notes);
    };

    const [styles] = React.useState(() => mergeToNew(cartLineNotesStyles, extendedStyles));

    const lineLabel = showLineNote ? "Hide Notes" : notes.length ? "Show Notes" : "Line Notes";

    return (
        <StyledWrapper {...styles.wrapper}>
            <Link
                {...styles.toggleLink}
                onClick={lineNotesClickHandler}
                data-test-selector="cartline_notesShowHideButton"
            >
                {translate(lineLabel)}
            </Link>
            {showLineNote && (
                <StyledWrapper {...styles.textAreaWrapper}>
                    <TextArea
                        {...styles.editableNotesTextArea}
                        value={notes}
                        onChange={changeHandler}
                        onBlur={blurHandler}
                        rows={2}
                        maxLength={300}
                        data-test-selector="cartline_notes"
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default WishlistLineNotes;
